import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { textoDaSkill } from "../utils/text";
import {
  AlertTriangle,
  ArrowRight,
  Loader2,
  Plug,
  RotateCcw,
  SlidersHorizontal,
} from "lucide-react";

import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { SkillsService, type SkillScopeTarget } from "../services/skillsService";
import { requirementLabel } from "../utils/requirement";

interface SkillUpdateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target: SkillScopeTarget;
  /** O nome da skill instalada. */
  name: string;
  /**
   * Para qual versão ir. Omitida, vai para a corrente do catálogo — que é o
   * caminho do botão de atualizar; informada, é o caminho de voltar.
   */
  version?: number;
  /** Roda a atualização de fato, depois de a pessoa confirmar. */
  onConfirm: () => void;
  saving: boolean;
}

/**
 * O que muda ao levar a instalação para a versão corrente (AB#85634).
 *
 * Trocar de versão troca **o procedimento que o time validou**. Fazer isso num
 * clique, sem dizer o que mudou, é o mesmo que trocar por baixo — a diferença
 * seria só quem apertou o botão.
 *
 * A parte que mais importa é a diferença de **parâmetros**: uma versão que
 * acrescenta parâmetro obrigatório precisa ser respondida depois, e uma que
 * remove descarta a resposta que estava gravada. Sem esta lista, a pessoa
 * descobre tarde — ou não descobre.
 */
export const SkillUpdateDialog = ({
  open,
  onOpenChange,
  target,
  name,
  version,
  onConfirm,
  saving,
}: SkillUpdateDialogProps) => {
  const { t, i18n } = useTranslation();

  const { data, isLoading } = useQuery({
    queryKey: [
      "skill_update_preview",
      target.scope,
      target.productId ?? "-",
      name,
      version ?? "latest",
    ],
    enabled: open && !!name,
    queryFn: () => SkillsService.updatePreview(target, name, version),
  });

  const voltando = data?.direction === "rollback";

  const temParametroNovoObrigatorio = (data?.parameters_added ?? []).some(
    (parametro) => parametro.required,
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {voltando && <RotateCcw className="h-4 w-4 text-muted-foreground" />}
            {t(voltando ? "skills.update.dialog.rollbackTitle" : "skills.update.dialog.title")}
            {data && (
              <span className="flex items-center gap-1.5 text-sm font-normal text-muted-foreground">
                <Badge variant="outline">v{data.from_version}</Badge>
                <ArrowRight className="h-3.5 w-3.5" />
                <Badge variant="secondary">v{data.to_version}</Badge>
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            {t(
              voltando
                ? "skills.update.dialog.rollbackDescription"
                : "skills.update.dialog.description",
            )}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : !data ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            {t("skills.update.dialog.upToDate")}
          </p>
        ) : (
          <div className="max-h-[50vh] space-y-5 overflow-y-auto">
            {/* As notas de TODAS as versões no caminho, e não só a da ponta:
                quem está na v2 indo para a v5 recebe as três de uma vez. Ao
                voltar são as mesmas notas, lidas ao contrário — o que deixa de
                valer —, e por isso ganham um título que diz isso. */}
            <div className="space-y-2">
              {voltando && (
                <p className="text-sm font-medium">{t("skills.update.dialog.undone")}</p>
              )}
              {data.changelogs.map((entrada) => (
                <div key={entrada.version} className="rounded-md border p-3">
                  <p className="text-xs font-medium">
                    {t("skills.version", { version: entrada.version })}
                  </p>
                  <p className="mt-0.5 text-sm text-muted-foreground">
                    {textoDaSkill(entrada.changelog, entrada.changelog_i18n, i18n.language) ||
                      t("skills.update.dialog.noNotes")}
                  </p>
                </div>
              ))}
            </div>

            {data.parameters_added.length > 0 && (
              <div className="space-y-2">
                <p className="flex items-center gap-1.5 text-sm font-medium">
                  <SlidersHorizontal className="h-4 w-4" />
                  {t("skills.update.dialog.parametersAdded")}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {data.parameters_added.map((parametro) => (
                    <Badge key={parametro.key} variant="secondary" className="font-normal">
                      {parametro.label}
                      {parametro.required && (
                        <span className="ml-1 text-[10px] uppercase">
                          {t("skills.parameters.required")}
                        </span>
                      )}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {data.parameters_removed.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium">{t("skills.update.dialog.parametersRemoved")}</p>
                <div className="flex flex-wrap gap-1.5">
                  {data.parameters_removed.map((parametro) => (
                    <Badge key={parametro.key} variant="outline" className="font-normal line-through">
                      {parametro.label}
                    </Badge>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  {t("skills.update.dialog.parametersRemovedHint")}
                </p>
              </div>
            )}

            {(data.requirements_added.length > 0 || data.requirements_removed.length > 0) && (
              <div className="space-y-2">
                <p className="flex items-center gap-1.5 text-sm font-medium">
                  <Plug className="h-4 w-4" />
                  {t("skills.update.dialog.requirements")}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {data.requirements_added.map((requisito) => (
                    <Badge key={requisito} variant="secondary" className="font-normal">
                      + {requirementLabel(requisito, t)}
                    </Badge>
                  ))}
                  {data.requirements_removed.map((requisito) => (
                    <Badge key={requisito} variant="outline" className="font-normal line-through">
                      {requirementLabel(requisito, t)}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* O aviso que decide se a pessoa atualiza agora ou depois: a skill
                fica sem responder um parâmetro que a versão nova exige. */}
            {temParametroNovoObrigatorio && !voltando && (
              <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-3">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-500" />
                <p className="text-xs text-muted-foreground">
                  {t("skills.update.dialog.requiredWarning")}
                </p>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>
            {t("common.cancel")}
          </Button>
          <Button onClick={onConfirm} disabled={saving || !data}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {/* O mesmo botão confirma os dois sentidos; o verbo tem de dizer
                qual deles, senão "atualizar" sairia num rollback. */}
            {data
              ? t(
                  voltando
                    ? "skills.update.dialog.confirmRollback"
                    : "skills.update.dialog.confirm",
                )
              : t("skills.update.dialog.title")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
